import React, { useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Moveable from "react-moveable";

import { useMoveableClipArea } from "../hooks/useMoveableClipArea";

import { Spacer, SubmitButton } from "@/components/common";
import { cn } from "@/lib/classNames";
import { Button } from "@/sx-layout/common/Button";
import { Select } from "@/sx-layout/common/Form";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/sx-layout/common/Modal";
import { ColorInput } from "@/sx-layout/components/ColorInput";
import { PresetColorPicker } from "@/sx-layout/components/PresetColorPicker";
import { toClip } from "@/sx-layout/components/settings/plotPlanMapping/util/plotPlanMappingAdapter";
import { usePlotPlans } from "@/sx-layout/components/settings/plotPlanSetting/hooks/usePlotPlans";
import { PlotPlanType } from "@/sx-layout/components/settings/plotPlanSetting/models";
import { usePlotPlanImage } from "@/sx-layout/hooks/usePlotPlanImage";
import { PlotPlanMapping } from "@/sx-layout/hooks/usePlotPlanMappingApi";
import { normalizeHexColor } from "@/sx-layout/utils";

export type Props = {
  readonly selected?: PlotPlanMapping;
  readonly existingDetailPlotPlanIds?: number[];
  readonly onSave: (props: {
    detailPlotPlanId: number;
    color: string;
    clip_y: number;
    clip_x: number;
    clip_w: number;
    clip_h: number;
    uploadfile: Blob;
  }) => void;
  readonly onClose: () => void;
};

export const PlotPlanDetailClipEditor = ({ selected, existingDetailPlotPlanIds, onSave, onClose }: Props) => {
  const { t } = useTranslation();

  const { plotPlans } = usePlotPlans();
  const plotPlanDetails = useMemo(
    () =>
      plotPlans.filter(
        (v) =>
          v.plot_plan_type === PlotPlanType.DETAIL &&
          (selected || !(existingDetailPlotPlanIds ?? []).includes(v.plot_plan_id))
      ),
    [plotPlans, existingDetailPlotPlanIds, selected]
  );

  const { control } = useForm({
    defaultValues: {
      plot_plan: selected?.detail_plot_plan_id ?? 0,
      color: normalizeHexColor(selected?.color) ?? "#cc0000",
    },
  });
  const [plotPlanId, setPlotPlanId] = useState(selected?.detail_plot_plan_id ?? 0);

  const { mapImageUrl, loading } = usePlotPlanImage(plotPlanId);

  const imageRef = useRef<HTMLImageElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const {
    area,
    initArea,
    onDrag,
    onDragEnd,
    onDragStart,
    onResizeStart,
    onResize,
    onResizeEnd,
    mapRatio,
    moveableRef,
  } = useMoveableClipArea({
    imageRef,
    areaRef,
  });

  const selectedColor = useWatch({ control, name: "color" });

  const cropClipImage = async (): Promise<Blob | null> => {
    const img = imageRef.current;
    if (!area || !img) return null;

    // 表示中の画像から実解像度へのスケール
    const scaleX = img.naturalWidth / img.clientWidth;
    const scaleY = img.naturalHeight / img.clientHeight;

    const sx = area.left * scaleX;
    const sy = area.top * scaleY;
    const sw = area.width * scaleX;
    const sh = area.height * scaleY;

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const saveClipArea = async () => {
    if (!area || !imageRef.current) return;
    const clip = {
      clip_y: Math.round(area.top / mapRatio),
      clip_x: Math.round(area.left / mapRatio),
      clip_w: Math.round(area.width / mapRatio),
      clip_h: Math.round(area.height / mapRatio),
    };
    const isClipChanged =
      !selected ||
      selected.clip_y !== clip.clip_y ||
      selected.clip_x !== clip.clip_x ||
      selected.clip_w !== clip.clip_w ||
      selected.clip_h !== clip.clip_h;
    const uploadfile = isClipChanged ? await cropClipImage() : null;
    onSave({
      detailPlotPlanId: plotPlanId,
      color: selectedColor,
      ...clip,
      uploadfile: uploadfile ?? undefined,
    });
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader>{t("edit_plot_plan_detail")}</ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <div className="flex items-center">
            <div className={cn("flex items-center justify-start")}>
              <label className={cn("text-right mr-[8px]")}>{t("plot_plan_detail")}</label>
              <div className="flex-col">
                <Select
                  className="w-220"
                  name="plot_plan"
                  control={control}
                  handleChange={(value) => setPlotPlanId(Number(value))}
                  options={plotPlanDetails.map((plotPlan) => ({
                    label: plotPlan.plot_plan_name,
                    value: plotPlan.plot_plan_id,
                  }))}
                  disabled={loading || !!selected}
                />
              </div>
            </div>
            <Spacer x="20px" />
            <Controller
              control={control}
              name={"color"}
              render={({ field: { onChange, value } }) => (
                <>
                  <div className="h-[28px]">
                    <ColorInput value={value} onChange={onChange} />
                  </div>
                  <Spacer x="10px" />
                  <div className="flex-col">
                    <p>{t("color_picker_description")}</p>
                    <PresetColorPicker onChange={onChange} />
                  </div>
                </>
              )}
            />
          </div>
          <div className="flex justify-center bg-[#ccc] overflow-hidden">
            <div className="relative h-[65vh]">
              {mapImageUrl && (
                <>
                  <img
                    ref={imageRef}
                    src={mapImageUrl}
                    alt="Plot Map"
                    className="block max-h-[65vh] w-auto object-contain"
                    onLoad={() => initArea(toClip(selected))}
                  />
                  <div
                    ref={areaRef}
                    style={{
                      position: "absolute",
                      top: area?.top ?? 0,
                      left: area?.left ?? 0,
                      width: area?.width ?? 0,
                      height: area?.height ?? 0,
                      backgroundColor: selectedColor,
                      opacity: 0.5,
                      border: `2px solid ${selectedColor}`,
                      boxSizing: "border-box",
                      cursor: "move",
                      display: area ? "block" : "none",
                    }}
                  />
                  {area && (
                    <Moveable
                      ref={moveableRef}
                      target={areaRef.current}
                      draggable={true}
                      resizable={!selected}
                      origin={false}
                      onDragStart={onDragStart}
                      onDrag={onDrag}
                      onDragEnd={onDragEnd}
                      onResizeStart={onResizeStart}
                      onResize={onResize}
                      onResizeEnd={onResizeEnd}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType="cancel" onClick={onClose} disabled={loading}>
          {t("cancel")}
        </Button>
        <SubmitButton onClick={saveClipArea} loading={loading} layout={true} disabled={!plotPlanId}>
          {t("save")}
        </SubmitButton>
      </ModalFooter>
    </Modal>
  );
};
